import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, Loader2, ArrowRight, TrendingUp, Sparkles, DollarSign, Calendar, Play, ChevronRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Calculator, 2: Form, 3: Success
  const [investment, setInvestment] = useState(50000);
  const [duration, setDuration] = useState(12);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showProjection, setShowProjection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setShowProjection(false);
      setIsAnimating(false);
    }
  }, [isOpen]);

  // Calculate projected growth (5% monthly compounding)
  const projectionData = useMemo(() => {
    const data = [];
    let currentValue = investment;
    const monthlyRate = 0.05;
    
    for (let i = 0; i <= duration; i++) {
      data.push({
        month: i === 0 ? 'Start' : `M${i}`,
        value: Math.round(currentValue),
        monthNum: i
      });
      currentValue *= (1 + monthlyRate);
    }
    return data;
  }, [investment, duration]);

  const finalValue = projectionData[projectionData.length - 1]?.value || investment;
  const totalGain = finalValue - investment;
  const percentageGain = ((totalGain / investment) * 100).toFixed(1);

  const handleCalculate = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setShowProjection(true);
      setIsAnimating(false);
    }, 800);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const enrichedMessage = `Investment Interest: $${investment.toLocaleString()} over ${duration} months.\n\n${formData.message}`;
      
      await axios.post(`${API}/enquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: enrichedMessage
      });
      
      // Show success state
      setIsSubmitting(false);
      setStep(3);
      
      // Auto close after showing success
      setTimeout(() => {
        onClose();
        // Reset state after modal closes
        setTimeout(() => {
          setStep(1);
          setShowProjection(false);
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 300);
      }, 4000);
    } catch (err) {
      setIsSubmitting(false);
      setError('Failed to submit. Please try again.');
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black/95 border border-amber-500/30 backdrop-blur-sm px-3 py-2 rounded-lg shadow-xl">
          <p className="text-amber-400 text-sm font-medium">
            ${payload[0].value.toLocaleString()}
          </p>
          <p className="text-white/50 text-xs">{payload[0].payload.month}</p>
        </div>
      );
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/90 backdrop-blur-md"
        onClick={() => !isSubmitting && onClose()}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-[#0a0a0a] to-[#111] border border-white/10 rounded-2xl shadow-2xl my-4 max-h-[90vh] overflow-y-auto">
        {/* Ambient glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-600/5 blur-3xl rounded-full pointer-events-none" />
        
        {/* Close Button */}
        <button 
          onClick={() => !isSubmitting && onClose()}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors z-20 p-1"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step Indicator */}
        <div className="relative z-10 px-6 pt-6 sticky top-0 bg-gradient-to-b from-[#0a0a0a] to-transparent pb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-500 ${
                  step >= s 
                    ? 'bg-amber-500 text-black' 
                    : 'bg-white/5 text-white/30'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-0.5 transition-all duration-500 ${
                    step > s ? 'bg-amber-500' : 'bg-white/10'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-8 text-[10px] text-white/40 tracking-wider">
            <span className={step >= 1 ? 'text-amber-500/80' : ''}>SIMULATE</span>
            <span className={step >= 2 ? 'text-amber-500/80' : ''}>DETAILS</span>
            <span className={step >= 3 ? 'text-amber-500/80' : ''}>DONE</span>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 px-6 pb-6">
          {/* Step 1: Calculator */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
                  <Sparkles className="w-3 h-3 text-amber-500" />
                  <span className="text-amber-500 text-[10px] font-medium tracking-wider">INTERACTIVE DEMO</span>
                </div>
                <h3 className="text-2xl font-light text-white mb-2">See Your Growth Potential</h3>
                <p className="text-white/40 text-sm">Enter your investment amount to visualize projected returns</p>
              </div>

              {/* Investment Input */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                  <label className="flex items-center gap-2 text-white/50 text-xs font-medium tracking-wider mb-3">
                    <DollarSign className="w-4 h-4 text-amber-500" />
                    INVESTMENT AMOUNT
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg">$</span>
                    <input
                      type="number"
                      value={investment}
                      onChange={(e) => {
                        setInvestment(Math.max(1000, parseInt(e.target.value) || 1000));
                        setShowProjection(false);
                      }}
                      className="w-full bg-black/50 border border-white/10 rounded-lg pl-8 pr-4 py-3 text-white text-xl font-light focus:outline-none focus:border-amber-500/50 transition-colors"
                      min="1000"
                      step="1000"
                    />
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="1000000"
                    step="10000"
                    value={investment}
                    onChange={(e) => {
                      setInvestment(parseInt(e.target.value));
                      setShowProjection(false);
                    }}
                    className="w-full mt-3 accent-amber-500"
                  />
                  <div className="flex justify-between text-[10px] text-white/30 mt-1">
                    <span>$10K</span>
                    <span>$1M</span>
                  </div>
                </div>

                <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
                  <label className="flex items-center gap-2 text-white/50 text-xs font-medium tracking-wider mb-3">
                    <Calendar className="w-4 h-4 text-amber-500" />
                    TIME PERIOD
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[6, 12, 24].map((months) => (
                      <button
                        key={months}
                        onClick={() => {
                          setDuration(months);
                          setShowProjection(false);
                        }}
                        className={`py-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                          duration === months
                            ? 'bg-amber-500 text-black'
                            : 'bg-black/50 border border-white/10 text-white/60 hover:border-amber-500/30'
                        }`}
                      >
                        {months}mo
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-white/30 text-xs mt-3">
                    {duration === 6 && '6 months projection'}
                    {duration === 12 && '1 year projection'}
                    {duration === 24 && '2 years projection'}
                  </p>
                </div>
              </div>

              {/* Calculate Button */}
              {!showProjection && (
                <button
                  onClick={handleCalculate}
                  disabled={isAnimating}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-medium tracking-wider rounded-xl flex items-center justify-center gap-2 hover:from-amber-400 hover:to-amber-500 transition-all duration-300 disabled:opacity-70"
                >
                  {isAnimating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      CALCULATING...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      CALCULATE PROJECTION
                    </>
                  )}
                </button>
              )}

              {/* Projection Results */}
              {showProjection && (
                <div className="animate-fade-in-up">
                  {/* Chart */}
                  <div className="bg-black/30 border border-white/5 rounded-xl p-4 mb-4">
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={projectionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="projectionGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                              <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <XAxis 
                            dataKey="month" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
                          />
                          <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
                            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                            width={50}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            fill="url(#projectionGradient)"
                            animationDuration={1500}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-center">
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">INVESTED</p>
                      <p className="text-white text-lg font-light">${investment.toLocaleString()}</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
                      <p className="text-amber-500/70 text-[10px] tracking-wider mb-1">PROJECTED VALUE</p>
                      <p className="text-amber-400 text-lg font-medium">${finalValue.toLocaleString()}</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
                      <p className="text-green-500/70 text-[10px] tracking-wider mb-1">TOTAL GAIN</p>
                      <p className="text-green-400 text-lg font-medium flex items-center justify-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        {percentageGain}%
                      </p>
                    </div>
                  </div>

                  <p className="text-white/30 text-[10px] text-center mb-4">
                    *Based on 5% average monthly returns. Past performance doesn't guarantee future results.
                  </p>

                  {/* CTA */}
                  <button
                    onClick={() => setStep(2)}
                    className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-medium tracking-wider rounded-xl flex items-center justify-center gap-2 hover:from-amber-400 hover:to-amber-500 transition-all duration-300"
                  >
                    GET PERSONALIZED DEMO
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Contact Form */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-light text-white mb-2">Almost There!</h3>
                <p className="text-white/40 text-sm">Enter your details for a personalized demo</p>
                <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <span className="text-amber-500/80 text-xs">Your interest: </span>
                  <span className="text-amber-400 text-xs font-medium">${investment.toLocaleString()} / {duration} months</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/50 text-[10px] font-medium tracking-wider mb-2">
                      FULL NAME *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[10px] font-medium tracking-wider mb-2">
                      EMAIL *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/50 text-[10px] font-medium tracking-wider mb-2">
                    PHONE NUMBER
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label className="block text-white/50 text-[10px] font-medium tracking-wider mb-2">
                    ANY QUESTIONS?
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-light placeholder-white/30 focus:outline-none focus:border-amber-500/50 transition-colors resize-none"
                    placeholder="I'd like to learn more about..."
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-xs text-center">{error}</p>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="px-6 py-3 border border-white/10 text-white/60 rounded-xl hover:border-white/20 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-medium tracking-wider rounded-xl flex items-center justify-center gap-2 hover:from-amber-400 hover:to-amber-500 transition-all duration-300 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        SUBMITTING...
                      </>
                    ) : (
                      <>
                        SCHEDULE MY DEMO
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="animate-fade-in text-center py-8">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-slow">
                <Check className="w-10 h-10 text-amber-500" />
              </div>
              <h3 className="text-2xl font-light text-white mb-3">You're All Set!</h3>
              <p className="text-white/50 text-sm max-w-sm mx-auto mb-4">
                Thank you for your interest! Our team will reach out within 24 hours to schedule your personalized demo.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Sparkles className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-sm">Confirmation sent to {formData.email}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
